import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Check, Copy, Download, KeyRound, Loader2, PlayCircle, RefreshCw, Send, Trash2, UserPlus } from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ButtonTip } from "@/components/ui/button-tip";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { onboardingAdminAPI, type AdminLeadDetail } from "../../OnboardingAPI";
import AnalysisReportView from "./AnalysisReportView";
import { errorText } from "./cockpitErrors";
import DeleteLeadDialog from "./DeleteLeadDialog";
import { LinkShownOnce } from "./InviteDialog";
import TenantDataBlock from "./TenantDataBlock";
import {
  ANALYSIS_LABEL,
  PROVISIONING_LABEL,
  PROVISIONING_VARIANT,
  STATUS_LABEL,
  STATUS_VARIANT,
  assetClassLabel,
  formatDateTime,
  formatSize,
  relativeTime,
  slotLabel,
  stepLabel,
} from "./leadLabels";

/** How often a running analysis is asked for. */
export const ANALYSIS_POLL_MS = 3000;
/** After this long a "running" analysis is taken for dead (the server lets a new start take over then). */
export const ANALYSIS_STALE_MS = 15 * 60 * 1000;
/** Polling stops after this long on a running analysis; the view then says so and offers a manual refresh. */
export const ANALYSIS_POLL_MAX_MS = 20 * 60 * 1000;

function Facts({ rows }: { rows: [string, ReactNode][] }) {
  return (
    <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-[10rem_1fr]">
      {rows.map(([label, value]) => (
        <div key={label} className="contents">
          <dt className="text-muted-foreground">{label}</dt>
          <dd className="min-w-0 whitespace-pre-wrap break-words">{value === undefined || value === "" ? "-" : value}</dd>
        </div>
      ))}
    </dl>
  );
}

function Block({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-3 border-t pt-5">
      <h3 className="text-base font-semibold">{title}</h3>
      {children}
    </section>
  );
}

/** A row only when there is something to show (an older invitation has more fields than a new one). */
function optionalRow(label: string, value: ReactNode | undefined): [string, ReactNode][] {
  return value === undefined || value === null || value === "" ? [] : [[label, value]];
}

/** The invited contact's name; when the invitation has none, the name the customer entered (marked as such), or nothing. */
function invitedContact(detail: AdminLeadDetail): ReactNode | undefined {
  const invited = detail.invitation.contactName?.trim();
  if (invited) return invited;
  const profile = detail.profile;
  const entered = [profile?.contactFirstName, profile?.contactLastName].map((part) => part?.trim()).filter(Boolean).join(" ") || profile?.contactName?.trim();
  return entered ? `${entered} (vom Kunden eingetragen)` : undefined;
}

/** The mobile number with the state of its check in words (an older server has no check: the number alone). */
function phoneText(detail: AdminLeadDetail): ReactNode {
  const check = detail.phoneVerification;
  const number = check?.phone || detail.profile.contactPhone;
  if (!check) return number;
  const state =
    check.state === "verified"
      ? `bestätigt${check.verifiedAt ? ` am ${formatDateTime(check.verifiedAt)}` : ""}`
      : check.state === "pending"
        ? "Code gesendet, noch nicht bestätigt"
        : "nicht bestätigt";
  return (
    <span data-testid="phone-check">
      {number ? `${number} (${state})` : state}
    </span>
  );
}

/** The state of the tenant in words. */
function provisioningText(provisioning: NonNullable<AdminLeadDetail["provisioning"]>): string {
  switch (provisioning.state) {
    case "pending":
      return "Mandant wird angelegt ...";
    case "done":
      if (provisioning.ssoOnly) return "Mandant angelegt. Die Kontaktperson meldet sich per SSO an.";
      return provisioning.credentialsMailSentAt
        ? `Mandant angelegt, Willkommensmail gesendet am ${formatDateTime(provisioning.credentialsMailSentAt)}.`
        : "Mandant angelegt, die Willkommensmail ist noch nicht gesendet.";
    case "existing_user":
      return "Für diese E-Mail-Adresse gibt es schon einen Zugang, es wurde nichts angelegt.";
    case "failed":
      return "Anlegen fehlgeschlagen.";
  }
}

const YES_NO: Record<string, string> = { yes: "Ja", no: "Nein", unknown: "Weiß nicht" };
const ESCAPE_STATUS: Record<string, string> = { current: "Aktuell", outdated: "Veraltet", unknown: "Weiß nicht", none: "Keine vorhanden" };
const OWNERSHIP: Record<string, string> = { owner: "Eigentümer", operator: "Betreiber", both: "Eigentümer und Betreiber" };

/**
 * Detail view of one lead in a side sheet: invitation (with the internal note), profile and logo,
 * answers, files per slot with download, the analysis and its report, and the three actions.
 * Uploads are shown as text only; a file is downloaded through the authenticated API, never linked.
 */
export default function LeadDetailSheet({
  leadId,
  onClose,
  onChanged,
  onError,
}: {
  leadId: string | null;
  onClose: () => void;
  onChanged: () => void;
  onError: (error: unknown) => void;
}) {
  const [detail, setDetail] = useState<AdminLeadDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<"analyze" | "link" | "teamMail" | "credentials" | "adopt" | "import" | "provision" | null>(null);
  const [dataHint, setDataHint] = useState<string | null>(null);
  const [provisionHint, setProvisionHint] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<"link" | "restart" | "delete" | "credentials" | "provision" | null>(null);
  const [credentialsHint, setCredentialsHint] = useState<string | null>(null);
  const [idCopied, setIdCopied] = useState(false);
  const [teamMailHint, setTeamMailHint] = useState<string | null>(null);
  const [newToken, setNewToken] = useState<string | null>(null);
  const [linkDays, setLinkDays] = useState("30");
  const [now, setNow] = useState(() => Date.now());
  const [pollGaveUp, setPollGaveUp] = useState(false);
  const linkDaysValid = Number.isInteger(Number(linkDays)) && linkDays.trim() !== "" && Number(linkDays) >= 1 && Number(linkDays) <= 365;
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const currentId = useRef<string | null>(null);
  currentId.current = leadId;
  // The callbacks come from a parent that may rebuild them on every render. Held in refs, they
  // cannot restart the load effect (which would wipe the open detail) or the polling interval.
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const load = useCallback(
    async (id: string, quiet = false) => {
      if (!quiet) setLoading(true);
      try {
        const loaded = await onboardingAdminAPI.get(id);
        if (currentId.current === id) setDetail(loaded);
      } catch (error) {
        if (!quiet) onErrorRef.current(error);
      } finally {
        if (!quiet && currentId.current === id) setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    setDetail(null);
    setNewToken(null);
    setConfirm(null);
    setTeamMailHint(null);
    setCredentialsHint(null);
    setDataHint(null);
    setIdCopied(false);
    if (leadId) void load(leadId);
  }, [leadId, load]);

  // While the analysis runs, ask again until it is done or failed; stop when the sheet closes.
  const analysisRunning = detail?.analysis.state === "running";
  useEffect(() => {
    if (!leadId || !analysisRunning) {
      setPollGaveUp(false);
      return;
    }
    const startedPolling = Date.now();
    const timer = setInterval(() => {
      const current = Date.now();
      setNow(current);
      // Not endless: a run that never ends (a dead pod) is not asked about for ever.
      if (current - startedPolling >= ANALYSIS_POLL_MAX_MS) {
        clearInterval(timer);
        setPollGaveUp(true);
        return;
      }
      void load(leadId, true).then(() => onChangedRef.current());
    }, ANALYSIS_POLL_MS);
    return () => clearInterval(timer);
  }, [leadId, analysisRunning, load]);

  // The logo needs the auth header, so it comes as a blob and never as a plain link, and it only ever
  // goes into an <img>. Which image types are allowed is the server's call (the `slots` of the public
  // view); nothing here names an extension.
  const logoFile = detail?.files.find((file) => file.slot === "logo");
  const logoFileId = logoFile?.id;
  const logoContentType = logoFile?.contentType;
  useEffect(() => {
    if (!leadId || !logoFileId) {
      setLogoUrl(null);
      return;
    }
    let cancelled = false;
    let url: string | null = null;
    onboardingAdminAPI
      .downloadFile(leadId, logoFileId)
      .then((blob) => {
        if (cancelled) return;
        const typed = logoContentType?.startsWith("image/") ? new Blob([blob], { type: logoContentType }) : blob;
        url = URL.createObjectURL(typed);
        setLogoUrl(url);
      })
      .catch(() => {
        if (!cancelled) setLogoUrl(null);
      });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [leadId, logoFileId, logoContentType]);

  const run = async (kind: "analyze" | "link" | "teamMail" | "credentials" | "adopt" | "import" | "provision", action: () => Promise<void>) => {
    setBusy(kind);
    try {
      await action();
    } catch (error) {
      onErrorRef.current(error);
      // The server refuses a step the lead is no longer in (a second tab, a finished analysis): show what is true now.
      const code = (error as { code?: string })?.code;
      if (code === "invalid_state" || code === "analysis_running") {
        if (currentId.current) void load(currentId.current, true);
      }
    } finally {
      setBusy(null);
    }
  };

  const startAnalysis = () =>
    leadId &&
    run("analyze", async () => {
      setConfirm(null);
      setDetail(await onboardingAdminAPI.analyze(leadId));
      onChangedRef.current();
    });

  // The "new lead" mail to the team failed at the submit: send it again. An older server has no such route (404).
  const resendTeamMail = () =>
    leadId &&
    run("teamMail", async () => {
      setTeamMailHint(null);
      try {
        const next = await onboardingAdminAPI.resendTeamMail(leadId);
        setDetail(next);
        onChangedRef.current();
        setTeamMailHint(next.notifications?.submittedMailSentAt ? "Die Team-Mail wurde gesendet." : "Die Team-Mail ist wieder nicht angekommen. Bitte später noch einmal versuchen.");
      } catch (error) {
        if ((error as { status?: number })?.status === 404) {
          setTeamMailHint("Das erneute Senden gibt es auf diesem Server noch nicht.");
          return;
        }
        throw error;
      }
    });

  // A new one-time password and the access mail again. The server refuses (409) when there is no tenant or the contact uses SSO.
  const resendCredentials = () =>
    leadId &&
    run("credentials", async () => {
      setConfirm(null);
      setCredentialsHint(null);
      try {
        const next = await onboardingAdminAPI.resendCredentials(leadId);
        setDetail(next);
        onChangedRef.current();
        // Success only on the server's word for it: an old time stamp of an earlier mail does not count.
        setCredentialsHint(
          next.credentialsResent === true
            ? "Die Zugangsdaten wurden neu gesendet. Das bisherige Passwort ist ungültig."
            : "Der Server hat nicht bestätigt, dass die Zugangsdaten gesendet wurden. Es wurde nichts als erfolgreich gemeldet.",
        );
      } catch (error) {
        const known = error as { status?: number; code?: string; detail?: string; message?: string };
        if (known?.status === 409 || known?.status === 404 || known?.status === 502) {
          // the server's own German sentence (invalid_state, mail_failed: the password stays as it was); what is true now is loaded again
          setCredentialsHint(errorText(error, "Das Senden der Zugangsdaten ist in diesem Stand nicht möglich."));
          if (currentId.current) void load(currentId.current, true);
          return;
        }
        throw error;
      }
    });

  // Moving the uploads into the tenant / making buildings and assets from them, again. A refusal (409) comes with a
  // German sentence: it is the hint in the block, not an error dialog, and what is true now is loaded again.
  const runData = (kind: "adopt" | "import", call: (id: string) => Promise<AdminLeadDetail>) =>
    leadId &&
    run(kind, async () => {
      setDataHint(null);
      try {
        setDetail(await call(leadId));
        onChangedRef.current();
      } catch (error) {
        const known = error as { status?: number; detail?: string; message?: string };
        if (known?.status === 409 || known?.status === 404) {
          setDataHint(errorText(error, "Das ist in diesem Stand nicht möglich."));
          if (currentId.current) void load(currentId.current, true);
          return;
        }
        throw error;
      }
    });
  const adoptFiles = () => runData("adopt", onboardingAdminAPI.adoptFiles);
  const startImport = () => runData("import", onboardingAdminAPI.startImport);

  const copyCustomerId = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setIdCopied(true);
    } catch {
      setIdCopied(false);
    }
  };

  // Makes what is missing after the submit (tenant, access mail, adoption of the files). A refusal (409) comes with a
  // German sentence: it is the hint in the block, not an error dialog, and what is true now is loaded again.
  const provisionNow = () =>
    leadId &&
    run("provision", async () => {
      setConfirm(null);
      setProvisionHint(null);
      try {
        setDetail(await onboardingAdminAPI.provision(leadId));
        onChangedRef.current();
      } catch (error) {
        const known = error as { status?: number };
        if (known?.status === 409 || known?.status === 404) {
          setProvisionHint(errorText(error, "Das ist in diesem Stand nicht möglich."));
          if (currentId.current) void load(currentId.current, true);
          return;
        }
        throw error;
      }
    });

  const issueLink = () =>
    leadId &&
    run("link", async () => {
      setConfirm(null);
      // Never a silent fallback: the confirm button is not available while the value is invalid.
      const result = await onboardingAdminAPI.regenerateLink(leadId, Number(linkDays));
      setNewToken(result.token);
      onChangedRef.current();
    });

  const download = async (fileId: string, name: string) => {
    if (!leadId) return;
    try {
      const blob = await onboardingAdminAPI.downloadFile(leadId, fileId);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = name;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      onErrorRef.current(error);
    }
  };

  // A run that has been "running" for longer than the server's takeover time is stuck: allow a restart.
  const startedAt = detail?.analysis.startedAt ? new Date(detail.analysis.startedAt).getTime() : NaN;
  const analysisStuck =
    analysisRunning && Number.isFinite(startedAt) && Math.max(now, Date.now()) - startedAt > ANALYSIS_STALE_MS;
  const canAnalyze =
    !!detail &&
    (detail.status === "submitted" || detail.status === "analyzing") &&
    (detail.analysis.state !== "running" || analysisStuck);
  const customerMailTo = detail?.customerMailTo ?? detail?.invitation.contactEmail;
  // The mail to the team is recorded when it went out; a lead past the submit without that record needs it again.
  const teamMailFailed =
    !!detail && !!detail.notifications && !detail.notifications.submittedMailSentAt && detail.status !== "invited" && detail.status !== "in_progress";
  const provisioning = detail?.provisioning;
  const canResendCredentials = !!provisioning && provisioning.state === "done" && !provisioning.ssoOnly;
  // after the release: what is still missing (tenant, access mail) is fetched by calling the release again
  // (`pending` exists only after the release: the server is working on it right now. There is nothing to call again
  // for it - a second release could meet the first - so it offers a reload instead, see below.)
  const submittedOrLater = !!detail && detail.status !== "invited" && detail.status !== "in_progress";
  const provisioningIncomplete =
    submittedOrLater &&
    !!provisioning &&
    (provisioning.state === "failed" || (provisioning.state === "done" && !provisioning.ssoOnly && !provisioning.credentialsMailSentAt));
  const restart = analysisStuck || detail?.analysis.state === "failed" || detail?.analysis.state === "done";
  // why "Analyse starten" is off - from the same conditions that turn it off
  const analyzeReason: string | null =
    busy !== null
      ? "Eine andere Aktion läuft noch."
      : !!detail && detail.status !== "submitted" && detail.status !== "analyzing"
        ? "Die Analyse ist erst nach dem Absenden des Kunden möglich."
        : analysisRunning && !analysisStuck
          ? "Die Analyse läuft bereits."
          : null;
  const filesBySlot = new Map<string, AdminLeadDetail["files"]>();
  detail?.files.forEach((file) => filesBySlot.set(file.slot, [...(filesBySlot.get(file.slot) ?? []), file]));

  return (
    <>
      <Sheet open={!!leadId} onOpenChange={(open) => !open && onClose()}>
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-3xl" data-testid="lead-detail">
          <SheetHeader>
            <div className="flex items-start justify-between gap-3 pr-8">
              <SheetTitle>{detail?.companyName ?? "Lead"}</SheetTitle>
              {/* also here, not only at the very end of a long view */}
              {detail && (
                <ButtonTip label={busy !== null ? "Eine andere Aktion läuft noch." : "Löscht den Lead endgültig; der angelegte Mandant bleibt bestehen"} disabled={busy !== null}>
<Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="-mt-1 h-8 shrink-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
                  onClick={() => setConfirm("delete")}
                  disabled={busy !== null}
                  data-testid="delete-lead-header"
                >
                  <Trash2 className="mr-1.5 h-4 w-4" aria-hidden="true" />
                  Löschen
                </Button>
</ButtonTip>
              )}
            </div>
            <SheetDescription>Detailansicht des Onboarding-Leads</SheetDescription>
          </SheetHeader>

          {loading && !detail && <Skeleton className="mt-6 h-40 w-full" />}

          {detail && (
            <div className="mt-6 space-y-6">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant={STATUS_VARIANT[detail.status]}>{STATUS_LABEL[detail.status]}</Badge>
                <Badge variant="outline">Analyse: {ANALYSIS_LABEL[detail.analysis.state]}</Badge>
              </div>

              <div className="flex flex-wrap gap-2" data-testid="lead-actions">
                <ButtonTip label={analyzeReason ?? (restart ? "Liest die Dateien noch einmal aus; ein vorhandener Report wird überschrieben" : "Liest die hochgeladenen Dateien aus und verändert nichts an ihnen")} disabled={!canAnalyze || busy !== null}>
<Button onClick={() => (restart ? setConfirm("restart") : void startAnalysis())} disabled={!canAnalyze || busy !== null}>
                  {busy === "analyze" || (analysisRunning && !analysisStuck) ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                  ) : (
                    <PlayCircle className="mr-2 h-4 w-4" aria-hidden="true" />
                  )}
                  {restart ? "Analyse neu starten" : "Analyse starten"}
                </Button>
</ButtonTip>
                <ButtonTip label={busy !== null ? "Eine andere Aktion läuft noch." : "Macht den bisherigen Link ungültig und stellt einen neuen aus"} disabled={busy !== null}>
<Button variant="outline" onClick={() => setConfirm("link")} disabled={busy !== null}>
                  <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
                  Neuen Link ausstellen
                </Button>
</ButtonTip>
              </div>

              <Block title="Einladung">
                <Facts
                  rows={[
                    ["Firma", detail.invitation.companyName],
                    // the invitation carries only company and address; the name is the customer's to enter in the profile
                    ...optionalRow("Ansprechpartner", invitedContact(detail)),
                    ["E-Mail", detail.invitation.contactEmail],
                    ...optionalRow("Assetklasse", detail.invitation.assetClass ? assetClassLabel(detail.invitation.assetClass) : undefined),
                    ["Erstellt", formatDateTime(detail.createdAt)],
                    ["Link gültig bis", formatDateTime(detail.tokenExpiresAt)],
                    ["Eingang", formatDateTime(detail.submittedAt)],
                    ...optionalRow("Interne Notiz", detail.invitation.note),
                    ...optionalRow("Absender (öffentlich)", detail.invitation.senderName),
                    ...optionalRow("Persönliche Nachricht (öffentlich)", detail.invitation.welcomeMessage),
                  ]}
                />
              </Block>

              <Block title="Aktivität">
                <Facts
                  rows={[
                    ["Geöffnet", detail.openCount === undefined ? undefined : String(detail.openCount)],
                    ["Erste Öffnung", detail.firstOpenedAt ? formatDateTime(detail.firstOpenedAt) : undefined],
                    ["Letzte Öffnung", detail.lastOpenedAt ? `${formatDateTime(detail.lastOpenedAt)} (${relativeTime(detail.lastOpenedAt)})` : undefined],
                    ["Letzte Aktivität", detail.lastActiveAt ? `${formatDateTime(detail.lastActiveAt)} (${relativeTime(detail.lastActiveAt)})` : undefined],
                    ["Weit gekommen bis", detail.furthestStep ? stepLabel(detail.furthestStep) : undefined],
                  ]}
                />
                {detail.recentOpens && detail.recentOpens.length > 0 && (
                  <div>
                    <h4 className="mb-1 text-sm font-medium">Letzte Öffnungen</h4>
                    <ul className="max-h-40 space-y-0.5 overflow-y-auto text-xs text-muted-foreground tabular-nums" data-testid="recent-opens">
                      {detail.recentOpens.slice(0, 20).map((opened, index) => (
                        <li key={`${opened}-${index}`}>{formatDateTime(opened)}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  Zählt Öffnungen der Seite im Browser; Vorschau-Crawler (Mail/Messenger) werden nicht gezählt, deine eigenen Testaufrufe schon.
                </p>
              </Block>

              <Block title="Mails">
                <Facts
                  rows={[
                    ["Willkommensmail geht an", customerMailTo],
                    ["Team-Mail beim Absenden", detail.notifications ? (detail.notifications.submittedMailSentAt ? `gesendet ${formatDateTime(detail.notifications.submittedMailSentAt)}` : "nicht gesendet") : undefined],
                    [
                      "Willkommensmail",
                      provisioning
                        ? provisioning.ssoOnly
                          ? "keine (Anmeldung per SSO)"
                          : provisioning.state === "done"
                            ? provisioning.credentialsMailSentAt
                              ? `gesendet ${formatDateTime(provisioning.credentialsMailSentAt)}`
                              : "nicht gesendet"
                            : "noch nicht fällig"
                        : undefined,
                    ],
                    // an older lead whose result mail went out keeps that line (display only)
                    ...optionalRow("Ergebnismail (älterer Lead)", detail.notifications?.reportMailSentAt ? `gesendet ${formatDateTime(detail.notifications.reportMailSentAt)}` : undefined),
                  ]}
                />
                {teamMailFailed && (
                  <div className="space-y-2">
                    <p role="alert" data-testid="team-mail-failed" className="text-sm text-destructive">
                      Die Team-Mail an das Team wurde nicht gesendet.
                    </p>
                    <ButtonTip label={busy !== null ? "Eine andere Aktion läuft noch." : "Sendet die Mail an das Team noch einmal"} disabled={busy !== null}>
<Button variant="outline" onClick={() => void resendTeamMail()} disabled={busy !== null}>
                      {busy === "teamMail" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : <Send className="mr-2 h-4 w-4" aria-hidden="true" />}
                      Team-Mail erneut senden
                    </Button>
</ButtonTip>
                  </div>
                )}
                {teamMailHint && (
                  <p role="status" data-testid="team-mail-hint" className="text-sm text-muted-foreground">
                    {teamMailHint}
                  </p>
                )}
              </Block>

              {!provisioning && submittedOrLater && (
                <Block title="Mandant und Zugang">
                  <p className="text-sm" data-testid="provisioning-text">Für diesen Lead wurde noch kein Mandant angelegt.</p>
                  <div className="flex flex-wrap gap-2">
                    <ButtonTip label={busy !== null ? "Eine andere Aktion läuft noch." : "Legt Mandant und Zugang an, schickt die Willkommensmail und übernimmt die Dateien"} disabled={busy !== null}>
<Button variant="outline" onClick={() => setConfirm("provision")} disabled={busy !== null} data-testid="provision-now">
                      {busy === "provision" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : <UserPlus className="mr-2 h-4 w-4" aria-hidden="true" />}
                      Mandanten jetzt anlegen
                    </Button>
</ButtonTip>
                  </div>
                  {provisionHint && (
                    <p role="status" className="text-sm text-muted-foreground" data-testid="provision-hint">
                      {provisionHint}
                    </p>
                  )}
                </Block>
              )}
              {provisioning && (
                <Block title="Mandant und Zugang">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant={PROVISIONING_VARIANT[provisioning.state]} data-testid="provisioning-state">{PROVISIONING_LABEL[provisioning.state]}</Badge>
                  </div>
                  <p className="text-sm" data-testid="provisioning-text">
                    {provisioningText(provisioning)}
                  </p>
                  <p className="text-sm text-muted-foreground" data-testid="provisioning-phone">
                    {detail.phoneVerification?.state === "verified"
                      ? "Die bestätigte Handynummer wird beim Anlegen am Nutzer hinterlegt."
                      : "Ohne bestätigte Handynummer bekommt der Nutzer keine Nummer."}
                  </p>
                  <Facts
                    rows={[
                      ["Mandant", provisioning.customerName],
                      [
                        "Mandanten-ID",
                        provisioning.customerId ? (
                          <span className="inline-flex flex-wrap items-center gap-2">
                            <code className="rounded bg-muted px-1.5 py-0.5 text-xs" data-testid="customer-id">{provisioning.customerId}</code>
                            <ButtonTip label={"Kopiert die Mandanten-ID in die Zwischenablage"} disabled={false}>
<Button type="button" variant="ghost" size="sm" className="h-7 px-2" onClick={() => void copyCustomerId(provisioning.customerId as string)}>
                              {idCopied ? <Check className="mr-1 h-3.5 w-3.5" aria-hidden="true" /> : <Copy className="mr-1 h-3.5 w-3.5" aria-hidden="true" />}
                              {idCopied ? "Kopiert" : "Kopieren"}
                            </Button>
</ButtonTip>
                          </span>
                        ) : undefined,
                      ],
                      ["Angelegt am", provisioning.completedAt ? formatDateTime(provisioning.completedAt) : undefined],
                      ["Willkommensmail", provisioning.state === "done" && !provisioning.ssoOnly ? (provisioning.credentialsMailSentAt ? `gesendet ${formatDateTime(provisioning.credentialsMailSentAt)}` : "nicht gesendet") : undefined],
                    ]}
                  />
                  {provisioning.ssoOnly && (
                    <p className="text-sm text-muted-foreground" data-testid="provisioning-sso">
                      Die Kontaktperson meldet sich per SSO an. Es wurde kein Passwort vergeben und es wird keines gesendet.
                    </p>
                  )}
                  {provisioning.error && (
                    provisioning.state === "failed" ? (
                      <p role="alert" className="text-sm text-destructive" data-testid="provisioning-error">
                        {provisioning.error}
                      </p>
                    ) : (
                      // a hint of the server next to a tenant that exists (the portal address is not set, a mail is open): calm, always shown
                      <p role="status" className="text-sm text-muted-foreground" data-testid="provisioning-error">
                        {provisioning.error}
                      </p>
                    )
                  )}
                  <div className="flex flex-wrap gap-2">
                    {provisioningIncomplete && (
                      <ButtonTip label={busy !== null ? "Eine andere Aktion läuft noch." : "Holt Fehlendes nach: Mandant, Willkommensmail, Dateiübernahme"} disabled={busy !== null}>
<Button variant="outline" onClick={() => void provisionNow()} disabled={busy !== null} data-testid="provisioning-catch-up">
                        {busy === "provision" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />}
                        Fehlendes nachholen
                      </Button>
</ButtonTip>
                    )}
                    {provisioning.state === "pending" && (
                      <ButtonTip label={busy !== null ? "Eine andere Aktion läuft noch." : "Lädt den Stand vom Server neu"} disabled={busy !== null}>
<Button variant="outline" onClick={() => leadId && void load(leadId, true)} disabled={busy !== null} data-testid="provisioning-reload">
                        <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
                        Neu laden
                      </Button>
</ButtonTip>
                    )}
                    {canResendCredentials && (
                      <ButtonTip label={busy !== null ? "Eine andere Aktion läuft noch." : "Setzt ein neues Einmalpasswort und schickt die Willkommensmail noch einmal"} disabled={busy !== null}>
<Button variant="outline" onClick={() => setConfirm("credentials")} disabled={busy !== null} data-testid="resend-credentials">
                        {busy === "credentials" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : <KeyRound className="mr-2 h-4 w-4" aria-hidden="true" />}
                        Willkommensmail erneut senden
                      </Button>
</ButtonTip>
                    )}
                  </div>
                  {credentialsHint && (
                    <p role="status" className="text-sm text-muted-foreground" data-testid="credentials-hint">
                      {credentialsHint}
                    </p>
                  )}
                </Block>
              )}

              <TenantDataBlock
                detail={detail}
                busy={busy}
                hint={dataHint}
                onAdopt={() => void adoptFiles()}
                onImport={() => void startImport()}
                onReload={() => leadId && void load(leadId, true)}
              />

              <Block title="Profil">
                {logoUrl && (
                  <img
                    src={logoUrl}
                    alt={`Logo ${detail.profile.companyName}`}
                    className="max-h-20 max-w-[220px] rounded border bg-white p-2 object-contain"
                    data-testid="lead-logo"
                  />
                )}
                <Facts
                  rows={[
                    ["Firma", detail.profile.companyName],
                    ["Rechtlicher Name", detail.profile.legalName],
                    [
                      "Anschrift",
                      [
                        [detail.profile.street, detail.profile.streetNumber].filter(Boolean).join(" "),
                        [detail.profile.postalCode, detail.profile.city].filter(Boolean).join(" "),
                        detail.profile.country,
                      ]
                        .filter(Boolean)
                        .join(", "),
                    ],
                    ["Website", detail.profile.website],
                    // the customer enters the two names apart (an older lead has only the contact name the server made)
                    ...(detail.profile.contactFirstName !== undefined || detail.profile.contactLastName !== undefined
                      ? ([
                          ["Vorname", detail.profile.contactFirstName],
                          ["Nachname", detail.profile.contactLastName],
                        ] as [string, ReactNode][])
                      : ([["Ansprechpartner", detail.profile.contactName]] as [string, ReactNode][])),
                    ["E-Mail", detail.profile.contactEmail],
                    ["Handynummer", phoneText(detail)],
                    ["Assetklasse", assetClassLabel(detail.profile.assetClass)],
                    ["Rolle", detail.profile.ownership ? (OWNERSHIP[detail.profile.ownership] ?? detail.profile.ownership) : undefined],
                  ]}
                />
              </Block>

              <Block title="Antworten">
                <Facts
                  rows={[
                    ["Anlagen-ID stabil", detail.answers.stableIds ? (YES_NO[detail.answers.stableIds] ?? detail.answers.stableIds) : undefined],
                    ["Etiketten angebracht", detail.answers.labelsInstalled ? (YES_NO[detail.answers.labelsInstalled] ?? detail.answers.labelsInstalled) : undefined],
                    ["Nummer auf dem Etikett", detail.answers.labelNumberNote],
                    ["Kritische Bereiche", detail.answers.criticalAreas],
                    ["Quellsystem", detail.answers.sourceSystem],
                    ["Planstand", detail.answers.floorplanVersionNote],
                    ["Flucht- und Rettungspläne", detail.answers.escapePlansStatus ? (ESCAPE_STATUS[detail.answers.escapePlansStatus] ?? detail.answers.escapePlansStatus) : undefined],
                    ["Hinweis zu den Fluchtplänen", detail.answers.escapePlansNote],
                  ]}
                />
              </Block>

              <Block title={`Dateien (${detail.files.length})`}>
                {detail.files.length === 0 && <p className="text-sm text-muted-foreground">Noch keine Dateien.</p>}
                {Array.from(filesBySlot.entries()).map(([slot, files]) => (
                  <div key={slot} data-testid={`files-${slot}`}>
                    <h4 className="mb-1 text-sm font-medium">{slotLabel(slot)}</h4>
                    <ul className="space-y-1">
                      {files.map((file) => (
                        <li key={file.id} className="flex items-center gap-3 rounded-md border px-3 py-2 text-sm">
                          <span className="min-w-0 flex-1 break-all">{file.originalName}</span>
                          {file.storage === "tenant" && (
                            <span className="shrink-0 text-xs text-muted-foreground" data-testid={`in-tenant-${file.id}`}>im Mandanten</span>
                          )}
                          <span className="shrink-0 text-xs text-muted-foreground">{formatSize(file.size)}</span>
                          <ButtonTip label={"Lädt die Datei herunter"} disabled={false}>
<Button
                            type="button"
                            size="sm"
                            variant="outline"
                            aria-label={`Herunterladen: ${file.originalName}`}
                            onClick={() => void download(file.id, file.originalName)}
                          >
                            <Download className="h-4 w-4" aria-hidden="true" />
                          </Button>
</ButtonTip>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </Block>

              <Block title="Analyse">
                {detail.analysis.state === "idle" && (
                  <p className="text-sm text-muted-foreground">
                    Die Analyse ist noch nicht gestartet. Sie liest die Dateien nur und verändert nichts.
                  </p>
                )}
                {detail.analysis.state === "running" && (
                  <p role="status" className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    Die Analyse läuft seit {formatDateTime(detail.analysis.startedAt)}.{" "}
                    {pollGaveUp ? "Diese Ansicht aktualisiert sich nicht mehr von selbst." : "Diese Ansicht aktualisiert sich selbst."}
                  </p>
                )}
                {analysisStuck && (
                  <p role="alert" data-testid="analysis-stuck" className="text-sm text-warning">
                    Die Analyse scheint zu hängen, neu starten.
                  </p>
                )}
                {pollGaveUp && leadId && (
                  <ButtonTip label={"Lädt die Ansicht neu"} disabled={false}>
<Button type="button" size="sm" variant="outline" onClick={() => void load(leadId)}>
                    <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
                    Jetzt aktualisieren
                  </Button>
</ButtonTip>
                )}
                {detail.analysis.state === "failed" && (
                  <p role="alert" className="text-sm text-destructive">
                    Die Analyse ist fehlgeschlagen{detail.analysis.error ? `: ${detail.analysis.error}` : "."} Sie lässt sich neu starten.
                  </p>
                )}
                {detail.analysis.report ? (
                  <AnalysisReportView report={detail.analysis.report} />
                ) : (
                  detail.analysis.state === "done" && <p className="text-sm text-muted-foreground">Kein Report vorhanden.</p>
                )}
              </Block>
              <div className="border-t pt-4">
                <ButtonTip label={busy !== null ? "Eine andere Aktion läuft noch." : "Löscht den Lead endgültig; der angelegte Mandant bleibt bestehen"} disabled={busy !== null}>
<Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                  onClick={() => setConfirm("delete")}
                  disabled={busy !== null}
                  data-testid="delete-lead"
                >
                  <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
                  Lead löschen
                </Button>
</ButtonTip>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog open={confirm === "credentials"} onOpenChange={(open) => !open && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Willkommensmail erneut senden?</AlertDialogTitle>
            <AlertDialogDescription>
              Es wird ein neues Einmalpasswort gesetzt. Das bisherige Passwort, auch ein vom Kunden gewähltes, wird ungültig, sobald die
              Willkommensmail an {customerMailTo} rausgeht. Hat der Kunde sein Passwort schon geändert oder ging gerade erst eine Mail raus,
              lehnt der Server das ab; sein Satz steht dann im Block.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={() => void resendCredentials()}>Neu senden</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <DeleteLeadDialog
        open={confirm === "delete"}
        leadId={leadId}
        companyName={detail?.companyName ?? ""}
        customerId={detail?.provisioning?.state === "done" ? detail.provisioning.customerId : undefined}
        adoptionState={detail?.adoption?.state}
        status={detail?.status}
        fileCount={detail?.files.length}
        onClose={() => setConfirm(null)}
        onDeleted={() => {
          setConfirm(null);
          onChangedRef.current();
          onClose();
        }}
        onError={(error) => onErrorRef.current(error)}
      />

      <AlertDialog open={confirm === "restart"} onOpenChange={(open) => !open && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Analyse neu starten?</AlertDialogTitle>
            <AlertDialogDescription>
              Die Dateien werden noch einmal ausgelesen. Ein vorhandener Report wird dabei überschrieben.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={() => void startAnalysis()}>Neu starten</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirm === "provision"} onOpenChange={(open) => !open && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mandanten jetzt anlegen?</AlertDialogTitle>
            <AlertDialogDescription>
              Für diesen Lead wird ein Mandant mit Admin-Zugang für die Kontaktperson angelegt; die Zugangsdaten gehen per E-Mail an {customerMailTo},
              und die hochgeladenen Dateien werden in den Datenraum des Mandanten übernommen. Gebäude und Anlagen importierst du danach nach Prüfung.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={() => void provisionNow()}>Mandanten anlegen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirm === "link"} onOpenChange={(open) => !open && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Neuen Link ausstellen?</AlertDialogTitle>
            <AlertDialogDescription>
              Der bisherige Link wird ungültig. Der neue wird nur einmal angezeigt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="lead-link-days">Gültigkeit in Tagen (1 bis 365)</Label>
            <Input
              id="lead-link-days"
              type="number"
              min={1}
              max={365}
              value={linkDays}
              aria-invalid={linkDaysValid ? undefined : true}
              aria-describedby={linkDaysValid ? undefined : "lead-link-days-error"}
              onChange={(event) => setLinkDays(event.target.value)}
            />
            {!linkDaysValid && (
              <p id="lead-link-days-error" role="alert" className="text-xs text-destructive">
                Bitte eine ganze Zahl von 1 bis 365 angeben.
              </p>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              disabled={!linkDaysValid}
              onClick={(event) => {
                if (!linkDaysValid) {
                  event.preventDefault();
                  return;
                }
                void issueLink();
              }}
            >
              Neuen Link ausstellen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!newToken} onOpenChange={(open) => !open && setNewToken(null)}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Neuer Link</DialogTitle>
            <DialogDescription>Der bisherige Link ist ab jetzt ungültig.</DialogDescription>
          </DialogHeader>
          {newToken && <LinkShownOnce token={newToken} />}
          <DialogFooter>
            <ButtonTip label="Schließt den Dialog"><Button onClick={() => setNewToken(null)}>Fertig</Button></ButtonTip>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
